import { useHistory } from 'react-router';
import { useRecoilState } from 'recoil';

import { Box, Button, Divider, Heading, Modal, Tag, TextButton } from '@zillow/constellation-10';
import { CosmosRenderer, ReviewRenderer } from '@zillow/cosmos';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';

import {
  getCosmosJourneyToken,
  useLeaseExtensionQuery,
  useLeaseQuery,
  useLeaseSummaryQuery,
  usePropertyInfoQuery,
  useReevaluateLeaseCompletionMutation,
} from '../../utils/queries';
import type { LeaseAction } from '../../models';
import { Lease, Property } from '../../models';
import LeaseActionButtonGroup from '../../components/LeaseActions/LeaseActionButtonGroup';
import LeaseExtension from '../../models/LeaseExtension';
import EditLeaseExtensionModal from '../../components/LeaseRenewal/Landlord/EditLeaseExtensionModal';
import { isEditLeaseExtensionModalOpenState } from '../../../atoms';
import { useState } from 'react';
import { ModalBodyFullHeightLoadingMask } from '../../constants/styleConstants';
import SummaryTitle from '../../components/LeaseSummary/SummaryTitle';
import { useLeasingUrlParams } from '../../hooks';

export default function RenewalWizardModal() {
  const history = useHistory();
  const { propertyId, leaseId, renewalId } = useLeasingUrlParams();

  const [isReviewRendererLoading, setIsReviewRendererLoading] = useState(false);

  const [, setIsEditLeaseExtensionModalOpen] = useRecoilState(isEditLeaseExtensionModalOpenState);

  const propertyInfoQuery = usePropertyInfoQuery(propertyId);
  const leaseQuery = useLeaseQuery(leaseId);
  const leaseExtensionQuery = useLeaseExtensionQuery(renewalId);
  const leaseSummaryQuery = useLeaseSummaryQuery(renewalId, LeaseArtifactType.LEASE_EXTENSION_RENEWAL);
  const reevaluateLeaseCompletionMutation = useReevaluateLeaseCompletionMutation();

  const leaseExtension = new LeaseExtension(leaseExtensionQuery?.data);

  async function generateJourneyToken() {
    return getCosmosJourneyToken(LeaseArtifactType.LEASE_EXTENSION_RENEWAL, renewalId);
  }

  function onDataSubmitted() {
    reevaluateLeaseCompletionMutation.mutateAsync({
      postData: {
        leaseArtifactId: renewalId,
        leaseArtifactType: LeaseArtifactType.LEASE_EXTENSION_RENEWAL,
      },
    });
  }

  function onClose() {
    history.push(Lease.getDashboardPath());
  }

  return (
    <>
      {leaseExtension.isEditable() ? (
        <Modal.Root
          size="fullScreen"
          open
          onOpenChange={(open) => {
            if (!open) {
              onClose();
            }
          }}
          useDismissProps={{ escapeKey: false, outsidePress: false }}
        >
          <Modal.Portal>
            <Modal.Backdrop>
              <Modal.Content css={{ padding: '0 !important' }}>
                <Modal.Body css={{ padding: '0 !important' }}>
                  {leaseExtensionQuery.isFetched && (
                    <CosmosRenderer
                      journeyId={leaseExtension?.journeyId}
                      journeyVersionId={leaseExtension?.journeyVersionId}
                      generateJourneyToken={generateJourneyToken}
                      onDataSubmitted={onDataSubmitted}
                      header={
                        <Heading level={1} css={{ textStyle: 'responsive.heading-xs', marginRight: 'tight' }}>
                          {propertyInfoQuery?.data?.streetAddress}
                        </Heading>
                      }
                      onSaveAndExit={onClose}
                      reviewNextElement={
                        <LeaseActionButtonGroup
                          leaseActions={leaseSummaryQuery?.data?.summaryActions as Array<LeaseAction>}
                          lease={leaseQuery?.data}
                          property={new Property(propertyInfoQuery?.data)}
                          isTop={false}
                        />
                      }
                    />
                  )}
                </Modal.Body>
              </Modal.Content>
            </Modal.Backdrop>
          </Modal.Portal>
        </Modal.Root>
      ) : (
        <Modal.Root
          size="fullScreen"
          open
          onOpenChange={(open) => {
            if (!open) {
              onClose();
            }
          }}
          useDismissProps={{ escapeKey: false, outsidePress: false }}
        >
          <Modal.Portal>
            <Modal.Backdrop>
              <Modal.Content>
                <Modal.Header
                  css={{
                    display: 'flex',
                    paddingLeft: { base: 'layout.tight', lg: 'layout.loosest' },
                    paddingRight: { base: 'layout.tight', lg: 'layout.loosest' },
                    paddingTop: 'layout.tight',
                    paddingBottom: 'layout.tight',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'layout.tight',
                  }}
                >
                  <Box
                    css={{
                      display: 'flex',
                      gap: { base: 'tighter', md: 'tight' },
                      flexDirection: { base: 'column', md: 'row' },
                      alignItems: { base: 'flex-start', md: 'center' },
                    }}
                  >
                    <Heading level={1} css={{ textStyle: 'responsive.heading-sm' }}>
                      Lease renewal for {propertyInfoQuery?.data?.streetAddress}
                    </Heading>
                    <Box>
                      <Tag tone={leaseExtension.getBadgeColor()} size="sm" css={{ textTransform: 'capitalize' }}>
                        {leaseExtension.getHumanReadableStatus()}
                      </Tag>
                    </Box>
                  </Box>
                  <Button tone="neutral" emphasis="outlined" onClick={onClose}>
                    Exit
                  </Button>
                </Modal.Header>
                <Divider />
                <Modal.Body>
                  <ModalBodyFullHeightLoadingMask loading={isReviewRendererLoading}>
                    {leaseExtensionQuery.isFetched && (
                      <Box
                        css={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(12, 1fr)',
                          marginTop: { base: 'none', lg: 'layout.looser' },
                        }}
                      >
                        <Box css={{ gridColumnEnd: { base: 'span 0', lg: 'span 1', xl: 'span 3' } }} />
                        <Box css={{ gridColumnEnd: { base: 'span 12', lg: 'span 10', xl: 'span 6' } }}>
                          <SummaryTitle
                            {...{
                              summaryText: leaseSummaryQuery?.data?.summaryText,
                              propertyAddress: propertyInfoQuery?.data?.streetAddress,
                              personFirstName: leaseExtensionQuery?.data?.landlords?.[0]?.firstName,
                              leaseStartDate: leaseExtension.getFriendlyStartDate(),
                              hasColandlord: leaseExtensionQuery?.data?.landlords?.length > 1,
                            }}
                          />
                          <ReviewRenderer
                            journeyId={leaseExtension?.journeyId}
                            journeyVersionId={leaseExtension?.journeyVersionId}
                            generateJourneyToken={generateJourneyToken}
                            isReadOnly
                            setIsReviewRendererLoading={setIsReviewRendererLoading}
                          />
                          <Box css={{ marginBottom: 'layout.tight' }} />
                        </Box>
                        <Box css={{ gridColumnEnd: { base: 'span 0', lg: 'span 1', xl: 'span 3' } }} />
                      </Box>
                    )}
                  </ModalBodyFullHeightLoadingMask>
                </Modal.Body>
                <Divider />
                <Modal.Footer
                  css={{
                    display: 'flex',
                    paddingLeft: { base: 'layout.tight', lg: 'layout.loosest' },
                    paddingRight: { base: 'layout.tight', lg: 'layout.loosest' },
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <LeaseActionButtonGroup
                    additionalButtons={
                      leaseExtension.isInSigningState() && (
                        <TextButton onClick={() => setIsEditLeaseExtensionModalOpen(true)}>Edit</TextButton>
                      )
                    }
                    leaseActions={leaseSummaryQuery?.data?.summaryActions as Array<LeaseAction>}
                    lease={leaseQuery?.data}
                    property={new Property(propertyInfoQuery?.data)}
                    isTop={false}
                  />
                </Modal.Footer>
              </Modal.Content>
            </Modal.Backdrop>
          </Modal.Portal>
        </Modal.Root>
      )}
      <EditLeaseExtensionModal />
    </>
  );
}
